import { useState } from 'react';
import axios from 'axios';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Wrench, RotateCcw } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Tab "Rese" del Magazzino: storico casse rese/dismesse con pulsante "Ricondiziona"
 * che le rimette in stato 'disponibile' dopo la riparazione.
 */
export default function CasseReseTab({ articoli, onRefresh }) {
  const rese = articoli.filter((a) => a.stato === 'reso' || a.stato === 'dismesso');
  const [ricondizionaTarget, setRicondizionaTarget] = useState(null);
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);

  const handleRicondiziona = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/magazzino/articoli/${ricondizionaTarget.id}/ricondiziona`, { note: nota || null });
      toast.success(`✅ ${ricondizionaTarget.descrizione} ricondizionata — di nuovo disponibile`);
      setRicondizionaTarget(null);
      setNota('');
      onRefresh?.();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nella ricondizione'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="border border-border overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Codice</th>
              <th>Descrizione</th>
              <th className="hidden md:table-cell">Matricola</th>
              <th>Stato</th>
              <th className="hidden md:table-cell">Ex Cliente</th>
              <th className="hidden lg:table-cell">Note</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rese.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted-foreground py-8">
                  Nessuna cassa resa o dismessa
                </td>
              </tr>
            ) : (
              rese.map((a) => (
                <tr key={a.id} data-testid={`resa-row-${a.id}`}>
                  <td className="font-mono">{a.codice}</td>
                  <td>
                    <div className="font-medium">{a.descrizione}</div>
                    {(a.marca || a.modello) && (
                      <div className="text-[10px] text-muted-foreground">{[a.marca, a.modello].filter(Boolean).join(' · ')}</div>
                    )}
                  </td>
                  <td className="hidden md:table-cell font-mono text-xs">{a.matricola || '-'}</td>
                  <td>
                    {a.stato === 'reso' ? (
                      <Badge className="rounded-none bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 text-[10px]">Reso</Badge>
                    ) : (
                      <Badge className="rounded-none bg-zinc-500/15 text-zinc-500 border border-zinc-500/30 text-[10px]">Dismesso</Badge>
                    )}
                  </td>
                  <td className="hidden md:table-cell text-xs">{a.venduto_cliente_nome || '-'}</td>
                  <td className="hidden lg:table-cell text-xs text-muted-foreground max-w-[280px]">
                    <div className="line-clamp-2 whitespace-pre-line" title={a.note}>{a.note || '-'}</div>
                  </td>
                  <td>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none text-emerald-700 dark:text-emerald-400 border-emerald-500/40"
                      onClick={() => setRicondizionaTarget(a)}
                      data-testid={`ricondiziona-${a.id}`}
                    >
                      <Wrench className="h-3.5 w-3.5 mr-1" />
                      Ricondiziona
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!ricondizionaTarget} onOpenChange={(v) => { if (!v) { setRicondizionaTarget(null); setNota(''); } }}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="ricondiziona-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono uppercase tracking-wider">
              <RotateCcw className="h-5 w-5 text-emerald-500" />
              Ricondiziona Cassa
            </DialogTitle>
            <DialogDescription>
              La cassa tornerà "Disponibile" in magazzino, pronta per essere riassegnata.
            </DialogDescription>
          </DialogHeader>
          {ricondizionaTarget && (
            <div className="p-3 bg-muted border border-border text-sm">
              <div className="font-medium">{ricondizionaTarget.descrizione}</div>
              <div className="text-xs text-muted-foreground font-mono mt-1">
                Matricola: {ricondizionaTarget.matricola || 'n/d'} · {[ricondizionaTarget.marca, ricondizionaTarget.modello].filter(Boolean).join(' · ')}
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs font-mono uppercase">Note riparazione (opzionale)</Label>
            <Textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              className="rounded-none"
              placeholder="Es. Sostituita testina di stampa"
              data-testid="ricondiziona-note"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => { setRicondizionaTarget(null); setNota(''); }}>Annulla</Button>
            <Button
              onClick={handleRicondiziona}
              disabled={saving}
              className="rounded-none font-mono uppercase"
              data-testid="ricondiziona-conferma-btn"
            >
              {saving ? 'Salvataggio...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
